/**
 * Выдача призов с упаковки.
 *
 * Экран кассира. Человек подошёл с крышкой, на которой написан приз, — кассир
 * нажимает кнопку с этим призом, и число выданного растёт на единицу.
 *
 * Больше объявленного выдать нельзя: опубликованное число — единственное, чем
 * обещание бренда в этой механике проверяется, и выдать сверх него значит
 * сделать его ложью. Кнопка на исчерпанном призе гаснет, а сервер отказывает
 * даже если нажать в обход.
 */
import type { ConsoleCampaignDetail, PackState } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Progress, Skeleton, fmtDateTime, num } from '@allswipes/ui';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { useResource } from '../hooks';
import { useToast } from '../toast';

export function PackScreen() {
  const { id = '' } = useParams();
  const toast = useToast();
  const detail = useResource<ConsoleCampaignDetail>(() => api.consoleCampaign(id), [id]);
  const pack = useResource<PackState>(() => api.consolePack(id), [id]);
  const [busy, setBusy] = useState<number | null>(null);
  const [point, setPoint] = useState('');

  const issue = async (index: number, label: string) => {
    setBusy(index);
    try {
      await api.consoleIssuePack(id, index, point);
      toast(`Выдано: ${label}`);
      pack.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не удалось отметить выдачу');
    } finally {
      setBusy(null);
    }
  };

  const campaign = detail.data?.campaign;
  const points = campaign?.config?.pickup ?? [];

  return (
    <Layout
      title="Выдача с упаковки"
      subtitle={campaign ? campaign.name : 'приз напечатан на упаковке'}
    >
      {pack.loading && <Skeleton count={3} />}

      {pack.data && (
        <>
          {points.length > 1 && (
            <Card style={{ marginBottom: 12 }}>
              <div className="lab" style={{ marginBottom: 6 }}>Точка выдачи</div>
              <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {points.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    className={`chip ${point === p.name ? 'on' : ''}`}
                    onClick={() => setPoint(point === p.name ? '' : p.name)}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              <p className="sub" style={{ fontSize: 11.5, marginTop: 6 }}>
                Попадёт в журнал выдачи. По нему потом видно, где призы расходятся быстрее.
              </p>
            </Card>
          )}

          <Card>
            <div className="row" style={{ marginBottom: 12 }}>
              <span className="h2">Что выдаём</span>
              <span className="spacer" />
              <Badge tone={pack.data.left > 0 ? 'good' : 'bad'}>
                {pack.data.left > 0 ? `осталось ${num(pack.data.left)}` : 'фонд исчерпан'}
              </Badge>
            </div>

            {pack.data.prizes.map((prize, i) => {
              const issued = prize.issued ?? 0;
              const left = Math.max(0, prize.count - issued);
              return (
                <div key={prize.label} style={{ marginBottom: 14 }}>
                  <div className="row" style={{ marginBottom: 6 }}>
                    <strong>{prize.label}</strong>
                    <span className="spacer" />
                    <span className="mono faint" style={{ fontSize: 12 }}>
                      {num(issued)} / {num(prize.count)}
                    </span>
                  </div>
                  <Progress value={issued} max={prize.count} />
                  <div className="row" style={{ marginTop: 8, gap: 8 }}>
                    <Button
                      variant="pri"
                      size="sm"
                      disabled={left === 0 || busy !== null}
                      onClick={() => void issue(i, prize.label)}
                    >
                      {busy === i ? 'Отмечаем…' : 'Выдал'}
                    </Button>
                    <span className="sub" style={{ fontSize: 12 }}>
                      {left > 0 ? `осталось ${num(left)}` : 'эти призы закончились'}
                    </span>
                  </div>
                </div>
              );
            })}
          </Card>

          <Card style={{ marginTop: 12 }}>
            <div className="h2" style={{ marginBottom: 6 }}>Последние выдачи</div>
            <p className="sub" style={{ marginBottom: 10 }}>
              Это те самые строки, по которым считается число на странице акции. Поправить его
              мимо них нельзя — в том и смысл.
            </p>
            {pack.data.issues.length === 0 ? (
              <Empty title="Пока ничего не выдавали" />
            ) : (
              <table className="tbl">
                <tbody>
                  {pack.data.issues.map((row) => (
                    <tr key={row.id}>
                      <td>{pack.data?.prizes[row.prize_index]?.label ?? '—'}</td>
                      <td className="faint">{row.point_name || '—'}</td>
                      <td className="faint">{row.issued_by}</td>
                      <td className="faint mono" style={{ fontSize: 12 }}>
                        {fmtDateTime(row.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </Layout>
  );
}
